import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'Why only 10 tickers — isn\'t diversification important?',
    a: 'It is, but 10 well-chosen tickers across 5-6 sectors is already more diversified than 90% of retail portfolios. Owning 50 stocks doesn\'t make you "more" diversified than owning 10 if those 50 are all large-cap US equities. Quality of diversification matters more than quantity.',
  },
  {
    q: 'Would these be the same 10 if I had $100K?',
    a: 'Yes for $10K, $50K, or $100K. The capital amount changes the position SIZE per ticker but not the choice of tickers. Below ~$5K total you might compress to 5-7 tickers for simplicity; above $100K you might split SCHD between SCHD and DGRO for extra coverage.',
  },
  {
    q: 'Why no international stocks or bonds?',
    a: 'For simplicity, this list is US dividend-focused. A more complete portfolio at higher capital levels would add 5-10% international (VYMI or individual ADRs) and a small bond allocation as you age. But the core dividend-growth thesis works without them; international and bonds are diversification overlay, not the foundation.',
  },
  {
    q: 'What if a ticker on the list cuts its dividend?',
    a: 'It can happen. The list is built around quality compounders with long streaks, but no streak is guaranteed. If one cuts: don\'t panic-sell on cut day (when the price will be down 20%+). Wait 30 days, evaluate whether the underlying business is permanently impaired, and rotate into a comparable peer if so.',
  },
  {
    q: 'How often should I update the list?',
    a: 'Rarely. The right 10 tickers for a new dividend investor are pretty stable over 5-10 year windows. The list might shift if a King cuts or a new vehicle (like a better successor ETF to SCHD) launches, but otherwise the foundation is durable.',
  },
];

export default function LearnFirstTenTickers() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="The First 10 Tickers — What I'd Buy If I Were Starting Today With $10K | DiviDrip"
      description="A specific, justifiable 10-ticker starter portfolio with reasoning per pick. Sector diversification, frequency mix, and yield/growth balance for a new dividend investor."
      canonical="https://twylightcrow.com/learn/first-10-tickers"
      guidePath="/learn/first-10-tickers"
      sectionLabel="Practical Workflows"
      sectionAnchor="workflows"
      kicker="Learn · Starter portfolio"
      h1="The First 10 Tickers — Starting With $10K"
      datePublished="2026-05-23"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/build-5k-monthly-income', label: 'Build a $5K monthly dividend income portfolio' },
        { to: '/learn/monthly-dividend-calendar', label: 'The monthly dividend calendar trick' },
        { to: '/learn/dividend-kings-vs-aristocrats-vs-achievers', label: 'Dividend Kings vs Aristocrats vs Achievers' },
        { to: '/learn/schd-vs-voo', label: 'SCHD vs VOO — dividend ETF vs total-market ETF' },
        { to: '/learn/boring-is-beautiful', label: 'Boring is beautiful — the long-game thesis' },
      ]}
    >
      <p>
        Asked a hundred ways: "If you were starting from zero today with $10,000, what
        would you buy?"
      </p>
      <p>
        Here's a specific, justifiable answer. Not THE answer — there is no one
        answer — but A defensible answer that someone could implement on a Monday and
        not regret on Friday.
      </p>

      <h2>The framework</h2>
      <ul>
        <li><strong>2 broad ETFs</strong> — the foundation; 40% of the portfolio</li>
        <li><strong>5 Dividend Kings or Aristocrats</strong> — quality compounders; 35% of the portfolio</li>
        <li><strong>1 quality REIT</strong> — sector + income tilt; 10% of the portfolio</li>
        <li><strong>1 covered-call ETF</strong> — current income; 10% of the portfolio</li>
        <li><strong>1 monthly-payer BDC</strong> — high-yield diversifier; 5% of the portfolio</li>
      </ul>
      <p>10 tickers, 5 buckets, 6 sectors represented.</p>

      <h2>The list — with reasoning</h2>

      <h3>The two ETFs ($4,000 — 40%)</h3>
      <ol>
        <li>
          <strong>SCHD ($2,500 — 25%)</strong> — Schwab's dividend-grower ETF. ~3.5%
          yield, ~10% historical dividend growth, 0.06% expense ratio. The
          unquestionable best starter dividend-growth holding. Click for the deep
          dive: <Link to="/learn/schd-vs-voo">SCHD vs VOO</Link>.
        </li>
        <li>
          <strong>DGRO ($1,500 — 15%)</strong> — iShares Core Dividend Growth ETF.
          Sister fund to SCHD with slightly different selection criteria; provides
          minor diversification benefit. Slightly higher quality bias.
        </li>
      </ol>

      <h3>The five Kings/Aristocrats ($3,500 — 35%)</h3>
      <ol start="3">
        <li>
          <strong>JNJ ($700) — Healthcare King.</strong> 60+ year dividend streak. ~3%
          yield. Anchors the healthcare sleeve.
        </li>
        <li>
          <strong>KO ($700) — Consumer staples King.</strong> 60+ year streak. ~3%
          yield. Anchor of consumer defensive.
        </li>
        <li>
          <strong>PG ($700) — Consumer staples King.</strong> 65+ year streak. ~2.5%
          yield. Complements KO with home/personal care.
        </li>
        <li>
          <strong>MSFT ($700) — Tech compounder.</strong> Not yet a Dividend Aristocrat
          (under 25 years of raises) but on the path. The "boring tech" exposure. ~0.8%
          yield but ~10% dividend growth.
        </li>
        <li>
          <strong>LOW ($700) — Consumer cyclical Aristocrat.</strong> 60+ year streak.
          ~2% yield. Home improvement adds a cyclical-but-quality flavour to the
          portfolio.
        </li>
      </ol>

      <h3>The income tilt ($2,500 — 25%)</h3>
      <ol start="8">
        <li>
          <strong>O ($1,000 — 10%) — Realty Income.</strong> The premier monthly-paying
          retail REIT. 30+ year streak. ~5.5% yield. Diversifies into real estate AND
          adds monthly cadence.
        </li>
        <li>
          <strong>JEPI ($1,000 — 10%) — JPMorgan Equity Premium Income.</strong>{' '}
          Covered-call ETF on S&P 500. ~8% yield, monthly distributions. Provides
          meaningful current income without taking on individual-stock risk.
        </li>
        <li>
          <strong>MAIN ($500 — 5%) — Main Street Capital.</strong> Premier monthly-paying
          BDC. ~6.5% yield + occasional supplemental distributions. The "if I had to
          own one BDC" choice.
        </li>
      </ol>

      <h2>What this gives you</h2>
      <ul>
        <li><strong>Blended yield:</strong> ~3.7% — ~$370/year on $10K</li>
        <li><strong>Sectors covered:</strong> Tech, Healthcare, Consumer Staples, Consumer Cyclical, Real Estate, Financials, broad-market via ETFs</li>
        <li><strong>Frequency:</strong> 7 quarterly payers + 3 monthly payers = 31 distinct payment events per year</li>
        <li><strong>Quality:</strong> 6 of 10 holdings have 25+ year dividend streaks</li>
        <li><strong>Total time to set up:</strong> ~30 minutes including account funding and 10 individual buys</li>
      </ul>

      <h2>What this list is NOT</h2>
      <ul>
        <li>❌ Optimal for someone needing income NOW (too growth-tilted; bump JEPI / O up if you need higher current yield)</li>
        <li>❌ Optimised for international diversification (no VYMI, no individual ADRs)</li>
        <li>❌ A "safe" portfolio in the absolute sense — every holding will drawdown in a bear market</li>
        <li>❌ A guarantee — even Dividend Kings can theoretically cut</li>
      </ul>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Build it in DiviDrip</h2>
      <p>
        Open <Link to="/dashboard?view=portfolio">DiviDrip</Link>, add these 10 tickers to your
        portfolio one by one (or use <strong>Import CSV</strong> from the Tool
        Box). The <strong>Portfolio Summary</strong> section at the top of the
        My Portfolio page will show your projected annual dividend income (and
        the "$X/mo" subtitle on the Annual Dividends tile), and the{' '}
        <strong>Calendar</strong> tool in the Tool Box will show payment events
        month-by-month. Watch how the income line grows as DRIP reinvestments
        compound.
      </p>
      <p className="text-xs text-muted-foreground italic mt-6">
        Not investment advice. Specific tickers are mentioned as worked examples, not
        recommendations. Do your own research before buying any security.
      </p>
    </LearnPage>
  );
}
